// Lucide icon helper for the map editor. Lucide exports each icon as
// `IconNode = [tag, attrs][]` — a flat array of SVG child elements (paths,
// circles, etc.) without the wrapping <svg>. We add the wrapper here and
// serialize to a string for inline injection into hand-rolled HTML.

import type { IconNode } from 'lucide';

export type { IconNode };

const SVG_DEFAULTS = {
  xmlns: 'http://www.w3.org/2000/svg',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round'
} as const;

function attrs(o: Record<string, string | number | undefined>): string {
  return Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${k}="${String(v).replace(/"/g, '&quot;')}"`)
    .join(' ');
}

/** Return an `<svg>...</svg>` markup string for a Lucide icon. Sized by the
 *  caller; stroke colour comes from the consumer via `currentColor`. */
export function renderIcon(icon: IconNode, size = 16, strokeWidth = 1.75): string {
  const inner = icon.map(([tag, a]) => {
    const merged = attrs(a as Record<string, string | number | undefined>);
    return `<${tag} ${merged} />`;
  }).join('');
  const head = attrs({
    ...SVG_DEFAULTS,
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    'stroke-width': strokeWidth
  });
  return `<svg ${head}>${inner}</svg>`;
}

export interface IconButtonOptions {
  icon: IconNode;
  title?: string;
  label?: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: (e: MouseEvent) => void;
  className?: string;
  size?: number;
  strokeWidth?: number;
}

export function iconButton(opts: IconButtonOptions): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = ['ed-iconbtn', opts.className ?? '', opts.active ? 'is-active' : ''].filter(Boolean).join(' ');
  if (opts.title) btn.title = opts.title;
  if (opts.disabled) btn.disabled = true;
  btn.innerHTML = renderIcon(opts.icon, opts.size ?? 16, opts.strokeWidth ?? 1.75) + (opts.label ? `<span class="ed-iconbtn-label">${opts.label}</span>` : '');
  if (opts.onClick) btn.addEventListener('click', opts.onClick);
  return btn;
}

export function iconSpan(icon: IconNode, size = 14): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'ed-icon';
  span.innerHTML = renderIcon(icon, size);
  return span;
}
